<template>
  <v-btn
    class="mbtn font-weight-regular"
    prepend-icon="ct:arrow-left"
    @click="mainBuyoutStep -= 1"
  >
    {{ t("buyout.appraisal.go_back") }}
  </v-btn>
  <div class="app__title app__title--h3">
    {{ t("buyout.common.buyout_bonus") }}
  </div>
  <div class="buyout__summary">
    <div class="wrapper">
      <template v-if="buyoutStore.bonusesLoading">
        <v-progress-circular />
      </template>
      <template v-else-if="!availableBonuses">
        <v-alert class="vAlert--warning">
          <template #title>
            {{ t("buyout.appraisal.no_bonus_apology") }}
          </template>
        </v-alert>
      </template>
      <template v-else>
        <v-alert class="vAlert--info">
          <template #title>
            {{ t("buyout.appraisal.get_buyout_bonus_global") }}
          </template>
          <div class="desc">
            <ul v-if="partnerStore.getBuyoutPartner">
              <li>{{ t('buyout.bonus.bonus_up_to', {max_bonus: useCurrency(maxBonusValue)}) }}</li>
              <li v-html="t('buyout.bonus.bonus_condition_partner', {partner_fe_name: `<strong>${getPartnerFeName}</strong>`, partner_link: getPartnerLink})" />
              <li>{{ t('buyout.bonus.bonus_bellow_select_partner') }}</li>
              <li v-html="t('buyout.bonus.bonus_no_new_device_partner', {partner_fe_name: getPartnerFeName })" />
              <li v-html="t('buyout.bonus.bonus_time_rule', {time: isO2Partner(partnerStore.getBuyoutPartner)? 30: 14 })" />
              <li v-if="isAnyO2AppleBonusActive()" v-html="t('buyout.bonus.apple_o2_text', {minimum_buyout_price: useCurrency(getO2AppleBonusMinimumBuyoutPrice())})" />
            </ul>
            <ul v-else>
              <li>{{ t('buyout.bonus.bonus_up_to', {max_bonus: useCurrency(maxBonusValue)}) }}</li>
              <li>{{ t('buyout.bonus.bonus_condition') }}</li>
              <li>{{ t('buyout.bonus.bonus_bellow_select') }}</li>
              <li>{{ t('buyout.bonus.bonus_no_new_device') }}</li>
              <li v-html="t('buyout.bonus.bonus_time_rule', {time: isO2Partner(buyoutStore.selectedBonusPartner)? 30: 14 })" />
            </ul>
          </div>
        </v-alert>
        <buyout-bonus-selector-base />
      </template>
      <buyout-send-email-window />
      <v-btn
        v-if="!authStore.isPartner && !buyoutStore.bonusesLoading"
        class="mbtn mbtn--green"
        size="x-large"
        :disabled="buyoutStore.buyoutBonusesLoading"
        :loading="buyoutStore.moveToPersonalDataLoading || buyoutStore.buyoutBonusesLoading"
        @click="moveToPersonalDataWithCheck"
      >
        {{ t('buyout.appraisal.continue_to_personal_info') }}
      </v-btn>
    </div>
    <buyout-device-summary-tile />
  </div>
</template>

<script setup lang="ts">
import useCurrency from "cashtec-common/src/composables/useCurrency";
import { storeToRefs } from "pinia";
import { maxBy, some, find } from "lodash-es";

const buyoutStore = useBuyoutStore();
const authStore = useAuthStore();
const partnerStore = usePartnerStore();
const { t } = useI18n();

const {
  mainBuyoutStep
} = storeToRefs(buyoutStore);

function moveToPersonalDataWithCheck () {
  if (!buyoutStore.validateNewDevice()) { return; }
  buyoutStore.moveToPersonalData();
}

const availableBonuses = computed(() => {
  return (partnerStore.getBuyoutPartner && buyoutStore.availableNewMetaMasters.length > 0) || buyoutStore.availableBonusPartners.length > 0;
});

const maxBonusValue = computed(() => {
  const maxBonusModel = partnerStore.getBuyoutPartner ? buyoutStore.availableNewMetaMasters : buyoutStore.availableBonusPartners;
  if (!maxBonusModel?.length || maxBonusModel.length === 0) {
    return 0;
  }
  // @ts-ignore
  const maxBonusValue = maxBy(maxBonusModel, "max_bonus_value");
  return maxBonusValue?.max_bonus_value ? maxBonusValue.max_bonus_value : 0;
});

const getPartnerFeName = computed<string>(() => {
  return partnerStore?.getBuyoutPartner?.fe_name ? partnerStore!.getBuyoutPartner!.fe_name : "";
});

const getPartnerLink = computed<string>(() => {
  return (partnerStore?.getBuyoutPartner?.link && partnerStore?.getBuyoutPartner?.link_description) ? `<a class="color--blueTone1 text-underline" href='${partnerStore!.getBuyoutPartner!.link}' target="_blank">${partnerStore!.getBuyoutPartner!.link_description}</a>` : "";
});

function isO2Partner(partner: PartnerDetail | PartnerBonus | null): boolean {
  return partner?.seo_name === "O2";
}

function isAnyO2AppleBonusActive(): boolean {
  if(partnerStore?.getBuyoutPartner){
    return some(partnerStore?.getBuyoutPartner?.active_bonuses, obj => appleO2Bonuses.value.includes(obj.id));
  }

  return false;
}

function getO2AppleBonusMinimumBuyoutPrice() : number {
  const appleBonus = find(partnerStore?.getBuyoutPartner?.active_bonuses, obj => appleO2Bonuses.value.includes(obj.id));
  return appleBonus ? appleBonus.min_price : 0;
}

// intentionally straightforward, a specialty for O2, we don’t have a way to dynamically link these bonuses, dynamic
// solution would require to have one sentence for every master bonus ( we connect two master bonuses here, and i don't want to create
// any Master of Master bonus only for this purpose )
const appleO2Bonuses = computed<string[]>(() => {
  return [
    "25d72091-680e-4244-9b1f-00a53af7331d",
    "7ef73b8d-e659-4b78-9811-ff918499bb65",
    "fb0a613d-274e-4191-a40c-af5026087588",
    "b54efaa1-915a-4432-bb23-92dc4d10dcf6"
  ];
});


if (availableBonuses.value && !buyoutStore.availableNewMetaMasters.length) {
  if (partnerStore!.getBuyoutPartner) {
    buyoutStore.selectedBonusPartner = buyoutStore.availableBonusPartners.find(partner => partner.id === partnerStore!.getBuyoutPartner!.id) || null;
    buyoutStore.loadAvailableNewMetaMasters();
  }
}

</script>
